import { useEffect, useRef, useState } from "react";
import { FormikProps } from "formik";
import { useTranslation } from "react-i18next";
import { toast } from "@outpave/ds";
import { titleCase } from "title-case";
import TransactionsType from "enums/TransactionsType";
import { LocalImageAsset } from "hooks/useAttachmentAndAssetHandling";
import { FormikCreateExpense } from "utils/reimbursements/reimbursementExpenseFormat";

/**
 * Shape of the fields the OCR extraction may return that we consume for
 * prefill. Everything is `unknown` because the OCR payload is untyped; we
 * narrow each value before using it.
 */
interface ExpenseOcrResult {
  merchantName?: unknown;
  amount?: unknown;
}

/**
 * Attachment + OCR behaviour for the reimbursement drawer: enforces the
 * receipt cap, converts/removes local assets, drives the accordion expand
 * state, and prefills merchant/amount from the OCR scan of the first cash
 * receipt.
 */
export function useReimbursementAttachments({
  formik,
  maxReceipts,
}: {
  formik: FormikProps<FormikCreateExpense>;
  maxReceipts: number;
}) {
  const { t } = useTranslation();
  const [ocrPrefilledFields, setOcrPrefilledFields] = useState<string[]>([]);
  const [isAccordionExpanded, setIsAccordionExpanded] = useState(true);

  const { expenseType, attachments, lostReceipt } = formik.values;

  // Clear the OCR prefill hint whenever the expense type changes (including
  // the reset-to-null on drawer open, and any explicit type switch).
  useEffect(() => {
    setOcrPrefilledFields([]);
  }, [expenseType]);

  const handleAddFiles = (files: LocalImageAsset[]) => {
    const remaining = maxReceipts - attachments.length;

    // Cap the total at maxReceipts: accept only up to the remaining slots and
    // warn when the selection overflowed (or the list was already full).
    if (remaining <= 0 || files.length > remaining) {
      toast.warning(
        t("You can attach up to {{count}} receipts", { count: maxReceipts }),
      );
    }
    if (remaining <= 0) return;

    if (lostReceipt) formik.setFieldValue("lostReceipt", false);

    const next = files.slice(0, remaining).map((file) => ({
      id: file?.id,
      name: file?.name,
      type: file?.type,
      assetUrl: file?.assetUrl,
      extension: file?.extension,
      file: file?.file,
    }));

    formik.setFieldValue("attachments", [...attachments, ...next]);
    setIsAccordionExpanded(true);
  };

  const handleRemove = (assetId: string) => {
    formik.setFieldValue(
      "attachments",
      attachments.filter((attachment) => attachment.id !== assetId),
    );
  };

  const handleLostReceiptChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    formik.setFieldValue("lostReceipt", e.target.checked);
    if (e.target.checked) {
      // Clear attachments when the user marks the receipt as missing.
      formik.setFieldValue("attachments", []);
    }
  };

  // Hold the latest formik in a ref so the async OCR callback prefills against
  // current values rather than a stale closure.
  const formikRef = useRef(formik);
  formikRef.current = formik;

  const prefillFromOcr = (ocrData: unknown) => {
    if (!ocrData || typeof ocrData !== "object") return;
    const data = ocrData as ExpenseOcrResult;

    const current = formikRef.current;
    const values = current.values;
    const filled: string[] = [];

    if (
      typeof data.merchantName === "string" &&
      data.merchantName &&
      "merchant" in values &&
      !values.merchant?.id &&
      !values.merchant?.name
    ) {
      current.setFieldValue("merchant", {
        id: "",
        name: titleCase(data.merchantName.toLowerCase()),
        logo: "",
        cachedLogo: "",
        url: "",
      });
      filled.push(t("Merchant"));
    }

    if (data.amount != null && !values.amount) {
      const parsedAmount = parseFloat(
        String(data.amount).replace(/[^0-9.]/g, ""),
      );
      if (!isNaN(parsedAmount) && parsedAmount > 0) {
        current.setFieldValue("amount", parseFloat(parsedAmount.toFixed(2)));
        filled.push(t("Amount"));
      }
    }

    if (filled.length > 0) setOcrPrefilledFields(filled);
  };

  const receiptRequired = expenseType === TransactionsType.CASH && !lostReceipt;

  const hasReceiptOrNotRequired = attachments?.length || !receiptRequired;

  // Scan for OCR only on the first receipt of a cash expense.
  const canUploadToOcr =
    expenseType === TransactionsType.CASH && attachments?.length === 0;

  return {
    ocrPrefilledFields,
    clearOcrPrefilled: () => setOcrPrefilledFields([]),
    isAccordionExpanded,
    setIsAccordionExpanded,
    handleAddFiles,
    handleRemove,
    handleLostReceiptChange,
    prefillFromOcr,
    receiptRequired,
    hasReceiptOrNotRequired,
    canUploadToOcr,
  };
}
